import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import './LegalPage.css';

function PrivacyPage() {
  return (
    <div className="legal-page">
      <SEO
        title="Privacy Policy"
        description="ASG Shading privacy policy - how we collect, use and protect your personal information under the Australian Privacy Act 1988."
        canonical="/privacy"
        jsonLd={[SEO.breadcrumbSchema([{ name: 'Privacy Policy' }]), SEO.webPageSchema({ type: 'WebPage', name: 'Privacy Policy', description: 'ASG Shading privacy policy.', url: '/privacy' })]}
      />
      <section className="legal-hero">
        <div className="legal-hero-bg"></div>
        <div className="legal-hero-content">
          <p className="legal-label">Legal</p>
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-updated">Last updated: 27 September 2026</p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Privacy Policy' }]} />

      <section className="legal-content">
        <div className="legal-inner">

          <h2>1. About This Policy</h2>
          <p>
            ASG Shading (ABN 54 658 871 369) ("we", "us", "our") is committed to protecting
            the privacy of your personal information. This policy explains how we collect, use,
            disclose and safeguard your information in accordance with the Australian Privacy Act
            1988 (Cth) and the Australian Privacy Principles (APPs).
          </p>

          <h2>2. Information We Collect</h2>
          <p>We may collect the following personal information when you interact with us:</p>
          <ul>
            <li><strong>Contact details</strong> - name, email address, phone number, suburb</li>
            <li><strong>Project details</strong> - product type, size requirements, attachment preferences, and any photos or measurements you send us</li>
            <li><strong>Communication records</strong> - messages submitted through our quote request form or email</li>
            <li><strong>Technical data</strong> - IP address, browser type, pages visited, and cookies (see Section 7)</li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use your personal information to:</p>
          <ul>
            <li>Respond to your enquiries and provide quotes</li>
            <li>Advise you remotely on product selection, sizing and fabric</li>
            <li>Communicate about your order during quoting, fabrication and delivery</li>
            <li>Send follow-up communications about our services (with your consent)</li>
            <li>Improve our website and customer experience</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2>4. How We Share Your Information</h2>
          <p>
            We do not sell or rent your personal information. We may share your information with:
          </p>
          <ul>
            <li><strong>Suppliers and delivery partners</strong> - only as needed to fulfil and deliver your order</li>
            <li><strong>Service providers</strong> - hosting, email, and analytics services that help us operate our business</li>
            <li><strong>Legal authorities</strong> - if required by law or to protect our legal rights</li>
          </ul>

          <h2>5. Data Security</h2>
          <p>
            We take reasonable steps to protect your personal information from misuse, interference,
            loss, and unauthorised access. Our website uses SSL/TLS encryption for all data
            transmission. Access to personal information is restricted to authorised personnel only.
          </p>

          <h2>6. Your Rights</h2>
          <p>Under the Australian Privacy Principles, you have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of any inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Opt out of marketing communications at any time</li>
            <li>Make a complaint about how we handle your information</li>
          </ul>
          <p>
            To exercise these rights, contact us at{' '}
            <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a> or call{' '}
            <a href="tel:+61422439499">0422 439 499</a>.
          </p>

          <h2>7. Cookies</h2>
          <p>
            Our website uses cookies and similar technologies to improve functionality and
            analyse usage. You can control cookies through your browser settings. We use:
          </p>
          <ul>
            <li><strong>Essential cookies</strong> - required for the website to function (e.g. session management)</li>
            <li><strong>Analytics cookies</strong> - help us understand how visitors use our site</li>
            <li><strong>Security cookies</strong> - used by Cloudflare for security and performance</li>
            <li><strong>Advertising cookies</strong> - Google Ads uses these to tell us when someone who clicked one of our ads goes on to place an order, request a quote, send a question or call us</li>
          </ul>

          <h2>8. Third-Party Services</h2>
          <p>Our website may use the following third-party services:</p>
          <ul>
            <li>Google reCAPTCHA - to prevent spam submissions</li>
            <li>Google Fonts - for typography</li>
            <li>Cloudflare - for security and content delivery</li>
            <li>Google Ads - to measure which of our ads lead to orders and enquiries</li>
          </ul>
          <p>These services may collect technical data as outlined in their own privacy policies.</p>
          <p>
            When you place an order or request a quote, the email address and phone number you gave us
            are hashed (converted to an unreadable code) in your browser and sent to Google, so Google
            can match the order or request to an ad click without receiving the details themselves.
            Google calls this enhanced conversions. You can manage how Google personalises ads at{' '}
            <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">adssettings.google.com</a>.
          </p>

          <h2>9. Changes to This Policy</h2>
          <p>
            We may update this privacy policy from time to time. Changes will be posted on this page
            with an updated "last updated" date. We encourage you to review this policy periodically.
          </p>

          <h2>10. Contact Us</h2>
          <p>
            If you have questions about this privacy policy or wish to make a complaint, contact us:
          </p>
          <ul>
            <li>Email: <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a></li>
            <li>Phone: <a href="tel:+61422439499">0422 439 499</a></li>
            <li>ABN: 54 658 871 369</li>
          </ul>
          <p>
            If you are unsatisfied with our response, you may lodge a complaint with the{' '}
            Office of the Australian Information Commissioner (OAIC) at{' '}
            <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">www.oaic.gov.au</a>.
          </p>

          <div className="legal-back">
            <Link to="/">← Back to Home</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPage;
